import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import useApi from 'vl-common/src/hooks/useApi';
import { isToday } from 'vl-common/src/lib/isToday';
import { Modal, DatePicker, TimePicker, Col, Form, Input, Select, Button } from 'antd';
import { useQuery, useMutation } from '@tanstack/react-query';
import useDebounceValue from 'vl-common/src/hooks/useDebounceValue';
import { z } from 'zod';
import { useSystemType, enumSystemTypes } from 'vl-common/src/context/provider.app';
import { Datestamp } from 'vl-common/src/components/Datestamp';
import moment from 'moment';

interface CreateTaskProps {
  task_type: string;
  task_sub_type?: string;
  patientLookupGuid: string;
  assignToGuid: string;
  caseSelectionId?: string;
  dateObject: Date;
  timeObject: Date;
  notes: string;
}

interface SubTypeProps {
  task_sub_type: string;
  task_sub_type_code: string;
}

interface AddTaskModalProps {
  show: boolean;
  onClose: () => void;
  onCreated: () => void;
  case_id?: string | null; // if case ID do simple form
  patient_guid?: string | null; // if case GUID do simple form
  client_case_id?: string | null; // if case ID do simple form
}

const AddTaskModalContent = ({
  onClose,
  onCreated,
  case_id,
  patient_guid,
  client_case_id
}: Omit<AddTaskModalProps, 'show'>) => {
  const { t } = useTranslation('common');
  const [form] = Form.useForm();
  const { Api, consumed } = useApi();
  const [systemType] = useSystemType();
  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });
  const isSimple = (case_id && patient_guid && client_case_id) || false;

  const [patientSearch, setPatientSearch] = useState('');
  const [patientLookupGuid, setPatientLookupGuid] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<Date>(new Date());
  const debouncePatientSearch = useDebounceValue(patientSearch, 500);
  const [disableAdminTypeMode, setDisableAdminTypeMode] = useState(false);

  const addTaskSchema = z.object({
    task_type: z.string(),
    task_sub_type: z.string().optional(),
    patientLookupGuid: disableAdminTypeMode ? z.string().optional() : z.string(),
    assignToGuid: z.string(),
    caseSelectionId: z.string().optional(),
    dateObject: z
      .date()
      .refine((date) => date >= new Date() || isToday(date), { message: 'Date cannot be in the past ' })
      .refine((date) => date !== null, { message: 'Date is required' }),
    timeObject: z.date(),
    notes: z.string()
  });

  const [subTypeData, setSubTypeData] = useState<SubTypeProps[]>([]);
  const { data: tasksMeta, isLoading: isTasksMetaLoading } = useQuery({
    queryKey: ['tasksMeta'],
    queryFn: async () => await Api.meta.getTaskMeta()
  });
  const { data: assignToList, isLoading: assignToListIsLoading } = useQuery({
    queryKey: ['assignToList'],
    queryFn: async () =>
      await Api.task.getTaskAssignee({
        page: 1,
        rows: 999,
        sort: 'fname',
        order: 'asc'
      })
  });
  const { data: patientList, isLoading: patientListIsLoading } = useQuery({
    queryKey: ['patientList', debouncePatientSearch],
    queryFn: async () => {
      const response = await Api.referrers.getPatientList({
        page: 1,
        rows: 100,
        order: 'asc',
        name: debouncePatientSearch
      });
      const { data } = response;
      const { users } = data;
      try {
        return users.map((item: any) => {
          return {
            value: `${item.first_name} ${item.last_name}`,
            user_guid: item.user_guid,
            email: item.email,
            membership_no: item.membership_no
          };
        });
      } catch (e) {
        if (!users) {
          consumed(e);
          return [];
        } else {
          throw e;
        }
      }
    },
    enabled: (debouncePatientSearch && debouncePatientSearch.length > 2) || false
  });
  const { data: caseList, isLoading: caseListIsLoading } = useQuery({
    queryKey: ['caseList', patientLookupGuid],
    queryFn: async () =>
      await Api.cases.searchForCases({
        rows: 100,
        patient: patientLookupGuid,
        first_incomplete: true,
        active: true
      }),
    enabled: !!patientLookupGuid
  });
  const { mutate: addTask, isPending: addTaskIsLoading } = useMutation({
    mutationKey: ['addTask'],
    mutationFn: async ({
      task_type,
      task_sub_type,
      patientLookupGuid,
      assignToGuid,
      caseSelectionId,
      dateObject,
      timeObject,
      notes
    }: CreateTaskProps) =>
      await Api.task.createATask({
        task_type,
        task_sub_type,
        patientLookupGuid,
        assignToGuid,
        caseSelectionId,
        dateObject,
        timeObject,
        notes
      }),
    onError: () => displayAlertMessage('Bad Request, Try again.'),
    onSuccess: onCreated
  });

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
    const t = setTimeout(() => {
      setShowAlertMessage({ visibility: false, msg });
      clearTimeout(t);
    }, 5000);
  };

  const onTaskTypeChange = (value: string, option: any) => {
    form.setFieldsValue({ task_sub_type: undefined });
    if (
      systemType === enumSystemTypes.admin ||
      systemType === enumSystemTypes.referrer ||
      systemType === enumSystemTypes['referrer-admin']
    ) {
      if (form.getFieldValue('task_type') === 'ADMIN') {
        assignToList.forEach((item: any) => {
          if (item.assignee === 'HBS Queue') {
            form.setFieldsValue({ assignToGuid: item.assignee_guid });
          }
        });
        form.setFieldsValue({ patientLookupGuid: null, caseSelectionId: null });
        setDisableAdminTypeMode(true);
      } else {
        setDisableAdminTypeMode(false);
      }
    }
    if (value === '') {
      setSubTypeData([]);
      return;
    }
    const subTypes = tasksMeta?.data?.meta[0].task_types.filter((type: any) => type.type_code === option.key)[0];
    if (subTypes?.task_sub_types?.length) {
      setSubTypeData(subTypes.task_sub_types);
    } else {
      setSubTypeData([]);
    }
  };

  const onPatientLookupChange = (value: string) => {
    form.setFieldsValue({ caseSelectionId: '' });
    if (value) {
      setPatientLookupGuid(value);
    } else {
      setPatientLookupGuid('');
    }
  };

  const clearFormErrors = () => {
    const fields = form.getFieldsValue();
    const fieldKeys = Object.keys(fields);
    const clearErrors = fieldKeys.map((key) => ({ name: key, errors: [] }));
    form.setFields(clearErrors);
  };

  const isFormValid = async () => {
    try {
      return await addTaskSchema.parseAsync({
        ...form.getFieldsValue(),
        patientLookupGuid: isSimple ? String(patient_guid) : String(patientLookupGuid) || undefined,
        caseSelectionId: isSimple ? String(case_id) : String(form.getFieldValue('caseSelectionId')),
        dateObject: selectedDate,
        timeObject: selectedTime
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorFields = error.issues.map((issue) => ({
          name: issue.path[0],
          errors: [issue.message]
        }));

        form.setFields(errorFields);
        return false;
      }
    }
  };
  const onFinish = async () => {
    clearFormErrors();
    const validatedData = await isFormValid();
    if (validatedData) {
      addTask(validatedData as CreateTaskProps);
    }
  };

  // when closing useEffect
  useEffect(() => {
    if (form) {
      form.resetFields();
      setPatientLookupGuid('');
      setSubTypeData([]);
      setSelectedDate(new Date());
      setSelectedTime(new Date());
      setDisableAdminTypeMode(false);
    }
  }, [form]);

  return (
    <div>
      <Modal
        centered
        data-testid="create-task-modal"
        className="vl-add-modal"
        title={`Create a Task ${isSimple ? 'for Case: ' + client_case_id : ''}`}
        open
        okText="Create"
        width="798px"
        onOk={form.submit}
        onCancel={() => {
          onClose();
        }}
        confirmLoading={addTaskIsLoading}
        footer={[
          <Button data-testid="create-task-cancel" key="cancel" onClick={onClose} style={{ fontWeight: '400' }}>
            {t('Cancel')}
          </Button>,
          <Button
            data-testid="create-task-submit"
            key="submit"
            type="primary"
            style={{ fontWeight: '400' }}
            onClick={form.submit}
          >
            {t('Create')}
          </Button>
        ]}
      >
        {form && (
          <Col xs="12">
            <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
            <Form
              form={form}
              name="create-task-form"
              onFinish={onFinish}
              labelCol={{ span: 16 }}
              wrapperCol={{ span: 24 }}
              layout="vertical"
              style={{ margin: '12px 0%', width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}
              initialValues={{
                dateObject: moment()
              }}
            >
              <Form.Item
                name="task_type"
                label={t('Type')}
                rules={[{ required: true, message: t('Task type is required') }]}
              >
                <Select
                  placeholder={t('Select a type')}
                  data-testid="create-task-select-task_type"
                  style={{ width: '100%' }}
                  disabled={isTasksMetaLoading}
                  onChange={onTaskTypeChange}
                  loading={isTasksMetaLoading}
                  allowClear
                >
                  {!isTasksMetaLoading &&
                    tasksMeta?.data?.meta[0].task_types.map((type: any) => (
                      <Select.Option key={type.type_code} value={type.type_code} label={type.task_type_desc}>
                        {type.task_type_desc}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
              <Form.Item name="task_sub_type" label={t('Sub Type Requested')}>
                <Select
                  placeholder={t('Select a sub type')}
                  disabled={!subTypeData.length}
                  data-testid="create-task-select-task_sub_type"
                  style={{ width: '100%' }}
                  allowClear
                >
                  {subTypeData.map((subType) => (
                    <Select.Option
                      key={subType.task_sub_type_code}
                      value={subType.task_sub_type_code}
                      label={subType.task_sub_type}
                    >
                      {subType.task_sub_type}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {!isSimple && (
                <Form.Item
                  key="patientlookup"
                  name="patientLookupGuid"
                  label={t('Patient Lookup')}
                  rules={disableAdminTypeMode ? [] : [{ required: true, message: t('Patient is required') }]}
                >
                  <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder={t('Search for a patient')}
                    data-testid="create-task-select-patientLookupGuid"
                    filterOption={false}
                    defaultActiveFirstOption={false}
                    onSearch={(value) => setPatientSearch(value)}
                    disabled={disableAdminTypeMode}
                    onChange={onPatientLookupChange}
                    loading={
                      ((patientListIsLoading && debouncePatientSearch && debouncePatientSearch.length > 2) || false) &&
                      !patientList
                    }
                  >
                    {(patientList || []).map((item: any) => {
                      return (
                        <Select.Option key={item.user_guid} value={item.user_guid} label={item.value}>
                          <div>
                            {item.value} &nbsp;
                            <span style={{ fontSize: '12px', color: '#b4b4b4' }}>
                              {t('Patient Reference:')}
                              {item.membership_no}
                            </span>
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              )}
              <Form.Item
                name="assignToGuid"
                label={t('Assign To')}
                rules={[{ required: true, message: t('Assignee is required') }]}
                style={{ gridColumn: `${isSimple ? 'span 2' : 'span 1'}` }}
              >
                <Select
                  placeholder={t('Select an assignee')}
                  data-testid="create-task-select-assignToGuid"
                  style={{ width: '100%' }}
                  disabled={assignToListIsLoading}
                  loading={assignToListIsLoading}
                  allowClear
                >
                  {assignToList?.data.assignee.map((assignee: any) => {
                    return (
                      <Select.Option
                        key={assignee.assignee_guid}
                        value={assignee.assignee_guid}
                        label={assignee.assignee}
                      >
                        {assignee.assignee}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              {!isSimple && (
                <Form.Item
                  name="caseSelectionId"
                  label={t('Case Selection ID')}
                  style={{ gridColumn: 'span 2' }}
                  rules={[
                    ({ getFieldValue }) => ({
                      required: getFieldValue('task_type') === 'BOOK-FOLLOW',
                      message: t('Case selection is required for booking appointments')
                    })
                  ]}
                >
                  <Select
                    placeholder={t('Select a case')}
                    disabled={!patientLookupGuid || disableAdminTypeMode}
                    data-testid="create-task-select-caseSelectionId"
                    style={{ width: '100%' }}
                    loading={(!!patientLookupGuid && caseListIsLoading) || false}
                    allowClear
                  >
                    {caseList?.data?.cases?.map((item: any) => {
                      return (
                        <Select.Option key={item.case_id} value={String(item.case_id)} label={item.cases_dropdown}>
                          <div>
                            {t('Case:')}
                            {item.client_case_id} &nbsp;
                            <span style={{ fontSize: '12px', color: '#b4b4b4' }}>
                              {item.cat_desc} -{' '}
                              <Datestamp
                                date={item.case_created_date}
                                format="Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57"
                              />
                            </span>
                          </div>
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              )}
              <Form.Item name="dateObject" label={t('Due Date')} required>
                <DatePicker
                  style={{ width: '100%' }}
                  data-testid="create-task-dateObject"
                  format="DD/MM/YYYY"
                  // add default value to current date as a Moment object
                  disabledDate={(current) => {
                    return current && current.valueOf() < Date.now() && !isToday(current.toDate());
                  }}
                  onChange={(momentDate) => {
                    momentDate && setSelectedDate(momentDate.toDate());
                  }}
                />
              </Form.Item>
              <Form.Item name="timeObject" label={t('Due Time')}>
                <TimePicker
                  style={{ width: '100%' }}
                  data-testid="create-task-timeObject"
                  hideDisabledOptions
                  format="HH:mm"
                  minuteStep={15}
                  onChange={(momentTime) => {
                    momentTime && setSelectedTime(momentTime.toDate());
                  }}
                />
              </Form.Item>
              <Form.Item
                name="notes"
                label={t('Notes')}
                style={{ gridColumn: 'span 2' }}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('Notes are required')
                  }
                ]}
              >
                <Input.TextArea data-testid="create-task-notes" placeholder={t('Enter notes')} />
              </Form.Item>
            </Form>
          </Col>
        )}
      </Modal>
    </div>
  );
};

const AddTaskModal = ({ show, ...rest }: AddTaskModalProps) => show && <AddTaskModalContent {...rest} />;

export default AddTaskModal;
